import React, { useState, useEffect } from 'react';
import { GlobalMicroButton } from '../../GlobalMicroButton';
import { StatusBadge } from '../../StatusBadge';
import styles from './TestCaseCard.module.css';
import { useLocale } from '../../../context/DynamicLocaleProvider';
import { useTheme } from '../../../context/ThemeProvider';
import { TestCase, TestStatus } from '../../../types';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../lib/firebase';

interface TestCaseCardProps {
  testCase: TestCase;
  projectId: string;
  onEdit?: (testCase: TestCase) => void;
  onDelete?: (testCaseId: string) => void;
  onRun?: (testCase: TestCase) => void;
  onViewTraceability?: (testCase: TestCase) => void;
  onEntityClick?: (entityId: string, entityType: 'URS' | 'FS') => void;
  compact?: boolean;
}

export const TestCaseCard: React.FC<TestCaseCardProps> = ({
  testCase,
  projectId,
  onEdit,
  onDelete,
  onRun,
  onViewTraceability,
  onEntityClick,
  compact = false
}) => {
  const { t } = useLocale();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const [isExpanded, setIsExpanded] = useState(false);
  const [entityTitles, setEntityTitles] = useState<Record<string, string>>({});

  // Fetch entity titles for connected entities
  useEffect(() => {
    const fetchEntityTitles = async () => {
      if (!projectId || !isExpanded) return;

      const titles: Record<string, string> = {};

      // Fetch URS titles
      if (testCase.links.urs_ids.length > 0) {
        try {
          const ursCol = collection(db, 'projects', projectId, 'urs');
          const ursSnapshot = await getDocs(ursCol);
          ursSnapshot.docs.forEach(doc => {
            const data = doc.data();
            const id = data.id || `URS-${doc.id.substring(0, 8)}`;
            if (testCase.links.urs_ids.includes(id)) {
              titles[id] = data.title || id;
            }
          });
        } catch (err) {
          if (process.env.NODE_ENV !== 'production') {
            console.error('Error fetching URS titles:', err);
          }
        }
      }

      // Fetch FS titles
      if (testCase.links.fs_ids.length > 0) {
        try {
          const fsCol = collection(db, 'projects', projectId, 'fs');
          const fsSnapshot = await getDocs(fsCol);
          fsSnapshot.docs.forEach(doc => {
            const data = doc.data();
            const id = data.id || `FS-${doc.id.substring(0, 8)}`;
            if (testCase.links.fs_ids.includes(id)) {
              titles[id] = data.title || id;
            }
          });
        } catch (err) {
          if (process.env.NODE_ENV !== 'production') {
            console.error('Error fetching FS titles:', err);
          }
        }
      }

      setEntityTitles(titles);
    };

    fetchEntityTitles();
  }, [projectId, testCase.links, isExpanded]);

  const getStatusBadge = (status: TestStatus | string) => {
    const statusMap: Record<string, { textKey: string; variant: 'success' | 'warning' | 'error' | 'info' }> = {
      Approved: { textKey: 'global_status_approved', variant: 'success' },
      Executed: { textKey: 'global_status_passed', variant: 'success' },
      Draft: { textKey: 'global_status_draft', variant: 'info' },
      Deviation: { textKey: 'global_status_failed', variant: 'error' },
      Retired: { textKey: 'global_status_rejected', variant: 'warning' }
    };
    const statusInfo = statusMap[status] || { textKey: status.toString(), variant: 'info' as const };
    return <StatusBadge text={t(statusInfo.textKey)} variant={statusInfo.variant} size="medium" />;
  };

  const formatDate = (date?: any) => {
    if (!date) return '';
    try {
      return new Date(date?.seconds ? date.seconds * 1000 : date).toLocaleDateString();
    } catch {
      return '';
    }
  };

  if (compact) {
    return (
      <div className={styles.testCaseCardCompact}>
        <div className={styles.compactHeader}>
          <span className={styles.testId}>{testCase.id}</span>
          {getStatusBadge(testCase.status)}
        </div>
        <p className={styles.compactTitle}>{testCase.title}</p>
      </div>
    );
  }

  const handleCardClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest(`.${styles.cardActions}`)) {
      return;
    }
    if (target.closest('button') && !target.closest(`.${styles.cardHeader}`)) {
      return;
    }
    if (target.closest(`.${styles.entityLinkItem}`)) {
      return;
    }
    setIsExpanded(!isExpanded);
  };

  return (
    <div
      id={`entity-${testCase.id}`}
      data-entity-card
      className={styles.testCaseCard}
      onClick={handleCardClick}
      style={{ cursor: 'pointer' }}
    >
      <div className={styles.cardHeader}>
        <div className={styles.headerLeft}>
          <h4 className={styles.testId}>
            {testCase.id}: {testCase.title}
          </h4>
        </div>
        <div className={styles.headerRight}>
          {getStatusBadge(testCase.status)}
          <div
            data-expand-trigger
            className={`${styles.chevron} ${isExpanded ? styles.chevronOpen : ''}`}
          >
            <img
              src={isDarkMode ? "/assets/icons/chevron.down.svg" : "/assets/icons/chevron.down.black.svg"}
              alt={isExpanded ? "Collapse" : "Expand"}
              width={12}
              height={12}
            />
          </div>
        </div>
      </div>

      <p className={styles.description}>{testCase.objective || testCase.title}</p>

      <div className={`${styles.testDetails} ${isExpanded ? styles.expanded : styles.collapsed}`}>
        <div className={styles.testDetailsInner}>

          {/* Inline meta fields 
          <div className={styles.inlineMeta}>
            {testCase.meta?.owner && (
              <>
                <span className={styles.metaValue}>{testCase.meta.owner}</span>
              </>
            )}
          </div>*/}

          {/* Objective */}
          {testCase.objective && (
            <div>
              <span className={styles.metaLabel}>{t('project_tab_objective') || 'Objective'}:</span>
              <span className={styles.metaValue}> {testCase.objective}</span>
            </div>
          )}

          {/* Preconditions */}
          {testCase.preconditions && testCase.preconditions.length > 0 && (
            <div>
              <span className={styles.metaLabel}>{t('project_tab_preconditions') || 'Preconditions'}:</span>
              <span className={styles.metaValue}> {testCase.preconditions.join(', ')}</span>
            </div>
          )}

          {/* Dates on separate line */}
          {(testCase.createdAt || testCase.updatedAt) && (
            <div className={styles.inlineMeta}>

              {/* Acceptance Criteria */}
              {testCase.acceptance_criteria && (
                <div>
                  <span className={styles.metaLabel}>{t('project_tab_acceptance_criteria') || 'Acceptance Criteria'}:</span>
                  <span className={styles.metaValue}> {testCase.acceptance_criteria}</span>
                </div>
              )}

              {/* Created At */}
              {testCase.createdAt && (
                <div>
                  <span className={styles.metaLabel}>{t('global_created')}:</span>
                  <span className={styles.metaValue}> {formatDate(testCase.createdAt)}</span>
                </div>
              )}

              {/* Updated At */}
              {testCase.updatedAt && (
                <div>
                  <span className={styles.metaLabel}>{t('global_updated')}:</span>
                  <span className={styles.metaValue}> {formatDate(testCase.updatedAt)}</span>
                </div>
              )}



              {/* Test Steps */}
              {testCase.steps && testCase.steps.length > 0 && (
                <div className={styles.testStepsSection}>
                  <span className={styles.entityGroupLabel}>{t('project_tab_test_steps') || 'Test Steps'}:</span>
                  <div className={styles.testStepsList}>
                    {testCase.steps.map((step, index) => (
                      <div key={index} className={styles.testStep}>
                        <div className={styles.stepNumber}>{step.step_no || index + 1}</div>
                        <div className={styles.stepContent}>
                          <div className={styles.stepAction}>
                            <strong>Action:</strong> {step.action}
                          </div>
                          <div className={styles.stepExpected}>
                            <strong>Expected:</strong> {step.expected_result}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Connected Entities */}
          {(testCase.links.urs_ids.length > 0 || testCase.links.fs_ids.length > 0) && (
            <div className={styles.connectedEntitiesSection}>
              {testCase.links.urs_ids.length > 0 && (
                <div className={styles.entityGroup}>
                  <span className={styles.entityGroupLabel}>{t('project_tab_linked_urs') || 'Linked URS'}:</span>
                  <div className={styles.entityList}>
                    {testCase.links.urs_ids.map((id) => (
                      <button
                        key={id}
                        type="button"
                        className={styles.entityLinkItem}
                        onClick={(e) => {
                          e.stopPropagation();
                          onEntityClick?.(id, 'URS');
                        }}
                      >
                        {id}: {entityTitles[id] || id}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {testCase.links.fs_ids.length > 0 && (
                <div className={styles.entityGroup}>
                  <span className={styles.entityGroupLabel}>{t('project_tab_linked_fs') || 'Linked FS'}:</span>
                  <div className={styles.entityList}>
                    {testCase.links.fs_ids.map((id) => (
                      <button
                        key={id}
                        type="button"
                        className={styles.entityLinkItem}
                        onClick={(e) => {
                          e.stopPropagation();
                          onEntityClick?.(id, 'FS');
                        }}
                      >
                        {id}: {entityTitles[id] || id}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Card actions */}
          <div
            className={styles.cardActions}
            onClick={(e) => e.stopPropagation()}
          >

            {/* Run button 
            {onRun && (
              <GlobalMicroButton 
                onClick={() => onRun(testCase)}
                icon={<img src={isDarkMode ? "/assets/icons/play.fill.svg" : "/assets/icons/play.fill.black.svg"} alt="" width={12} height={12} />}
              >
                {t('global_run_test')}
              </GlobalMicroButton>
            )}*/}

            {/* Edit button */}
            {onEdit && (
              <GlobalMicroButton
                onClick={() => onEdit(testCase)}
                icon={<img src={isDarkMode ? "/assets/icons/pencil.svg" : "/assets/icons/pencil.black.svg"} alt="" width={12} height={12} />}
              >
                {t('global_edit')}
              </GlobalMicroButton>
            )}

            {/* Delete button */}
            {onDelete && (
              <GlobalMicroButton
                onClick={() => onDelete(testCase.id)}
                icon={<img src={isDarkMode ? "/assets/icons/trash.svg" : "/assets/icons/trash.black.svg"} alt="" width={12} height={12} />}
              >
                {t('global_delete')}
              </GlobalMicroButton>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

